import { createClient } from '@supabase/supabase-js'
import { logger } from './logger'

// Environment validation for browser runtime
const isDevelopment = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.port === '5173'
);

// Simple environment validation
if (isDevelopment) {
  logger.info('🔧 Environment Configuration:');
  logger.info('- Supabase URL:', import.meta.env?.VITE_SUPABASE_URL ? '✅ Set' : '❌ Missing');
  logger.info('- Supabase Key:', import.meta.env?.VITE_SUPABASE_ANON_KEY ? '✅ Set' : '❌ Missing');
}

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL || 'https://wavpctnckvekkmcjlnyc.supabase.co'
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  logger.error('❌ Missing Supabase environment variables')
  logger.error('Please check your .env file has VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY')
  logger.warn('⚠️ Using fallback Supabase configuration')
} else {
  logger.info('✅ Supabase configuration loaded successfully')
}

const STORAGE_KEY = 'trestune.auth';
const REMEMBER_ME_KEY = 'trestune.rememberMe';

type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

const safeNoopStorage: StorageLike = {
  getItem: () => null,
  setItem: () => { },
  removeItem: () => { },
};

/**
 * Select storage based on "remember me" flag.
 * - true (default): localStorage (persists across browser restarts)
 * - false: sessionStorage (cleared on tab/window close)
 */
function getPreferredStorage(): StorageLike {
  try {
    if (typeof window === 'undefined') return safeNoopStorage;
    const remember = window.localStorage.getItem(REMEMBER_ME_KEY);
    const preferLocal = remember !== 'false'; // default to localStorage if not set
    return preferLocal ? window.localStorage : window.sessionStorage;
  } catch {
    return safeNoopStorage;
  }
}

// Adapter delegates to preferred storage at call time (works even if flag changes after client creation)
const storageAdapter: StorageLike = {
  getItem: (key) => getPreferredStorage().getItem(key),
  setItem: (key, value) => getPreferredStorage().setItem(key, value),
  removeItem: (key) => getPreferredStorage().removeItem(key),
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: false, // Disable automatic token refresh to prevent excessive calls
    persistSession: true,
    storageKey: STORAGE_KEY,
    detectSessionInUrl: true,
    storage: storageAdapter,
    flowType: 'implicit', // Changed from 'pkce' to fix cross-browser email verification
  },
  global: {
    headers: {
      'X-Client-Info': 'trestune-app',
    },
  },
})

// Auth helpers with improved error handling
export const signUp = async (email: string, password: string, options?: { data?: any }) => {
  try {


    // Use the correct redirect URL based on environment
    const getRedirectUrl = () => {
      if (typeof window !== 'undefined') {
        // Dynamically use current origin for ALL environments (localhost, staging, production)
        return `${window.location.origin}/email-verification`;
      }
      // Fallback for server-side rendering
      return 'https://www.trestune.com/email-verification';
    };


    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        ...options,
        emailRedirectTo: getRedirectUrl(),
      },
    })

    return { data, error }
  } catch (err) {
    console.error('❌ SignUp error:', err)
    return { data: null, error: err }
  }
}

export const signIn = async (email: string, password: string) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  } catch (err) {
    console.error('SignIn error:', err)
    return { data: null, error: err }
  }
}

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut()
    return { error }
  } catch (err) {
    console.error('SignOut error:', err)
    return { error: err }
  }
}

export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  } catch (err) {
    console.error('GetCurrentUser error:', err)
    return { user: null, error: err }
  }
}

export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  return supabase.auth.onAuthStateChange(callback)
}

// Test connection on startup (non-blocking)
try {
  supabase.auth.getSession().then(({ data, error }) => {
    if (error) {
      console.warn('⚠️ Supabase connection issue:', error.message)
    } else {
      console.log('✅ Supabase connected successfully')
    }
  }).catch((err) => {
    console.error('❌ Failed to connect to Supabase:', err)
  })
} catch (err) {
  console.error('❌ Failed to initialize Supabase connection test:', err)
}

// Add global error handler for unhandled promise rejections
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    console.error('🚨 Unhandled promise rejection:', event.reason);
  });

  window.addEventListener('error', (event) => {
    console.error('🚨 Global error:', event.error);
  });
}

// Password reset helpers
export const requestPasswordReset = async (email: string) => {
  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/reset-password` : undefined,
    });
    return { data, error };
  } catch (err) {
    console.error('Password reset request error:', err);
    return { data: null, error: err };
  }
};

export const updateUserPassword = async (newPassword: string) => {
  try {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    return { data, error };
  } catch (err) {
    console.error('Password update error:', err);
    return { data: null, error: err };
  }
};

// Google OAuth authentication
export const signInWithGoogle = async () => {
  try {
    // Determine the correct redirect URL based on environment
    const getRedirectUrl = () => {
      if (typeof window === 'undefined') return 'https://www.trestune.com';
      // Dynamically use current origin for ALL environments
      return `${window.location.origin}/auth/callback`;
    };

    const redirectTo = getRedirectUrl();

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        }
      }
    });

    if (error) {
      console.error('🔴 Error signing in with Google:', error);
      return { data: null, error };
    }


    return { data, error: null };
  } catch (err) {
    console.error('🔴 Google sign-in error:', err);
    return { data: null, error: err };
  }
};

// Check if user is new (for redirect logic)
export const isNewUser = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('user_id', userId)
      .single();

    if (error && error.code === 'PGRST116') {
      // No profile found, user is new
      return true;
    }

    if (error) {
      console.error('Error checking user profile:', error);
      return false;
    }

    // Profile exists, user is returning
    return false;
  } catch (err) {
    console.error('Error checking if user is new:', err);
    return false;
  }
};

// Create user profile for new users
export const createUserProfile = async (user: any) => {
  try {


    // Extract name information
    const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
    const firstName = user.user_metadata?.first_name || user.user_metadata?.given_name || '';
    const lastName = user.user_metadata?.last_name || user.user_metadata?.family_name || '';

    // Prepare basic profile data (no automatic type assignment)
    const profileData = {
      user_id: user.id,
      email: user.email,
      name: fullName || user.email?.split('@')[0] || 'User',
      first_name: firstName,
      last_name: lastName,
      profile_photo_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || '',
      // Remove automatic profile type assignment - let users choose in profile selection
      bio: user.user_metadata?.bio || '',
      location: user.user_metadata?.locale || '',
      linkedin: user.user_metadata?.linkedin_url || '',
      github: user.user_metadata?.github_url || '',
      twitter: user.user_metadata?.twitter_url || '',
      website: user.user_metadata?.website || '',
      is_public: true,
      show_email: true,
      show_phone: false,
      show_location: true,
      allow_messages: true,
      allow_connections: true,
      email_notifications: true,
      push_notifications: true,
      messaging_enabled: true,
      allow_follower_messages: true,
      email_confirmed: user.email_confirmed_at ? true : false,
      profile_completed: false, // Force profile selection for all new users
      profile_completion_score: 0,
      verification_status: 'unverified',
      language: 'en',
      timezone: 'UTC',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };



    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(profileData, {
        onConflict: 'user_id',
        ignoreDuplicates: false
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating/updating user profile:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details
      });
      return { data: null, error };
    }


    return { data, error: null };
  } catch (err) {
    console.error('❌ Exception creating user profile:', err);
    return { data: null, error: err };
  }
};

// Manual token refresh function for when we need to refresh tokens
export const refreshSession = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.warn('Token refresh failed:', error);
      return { data: null, error };
    }
    return { data, error: null };
  } catch (err) {
    console.error('Token refresh error:', err);
    return { data: null, error: err };
  }
}

// Check if token needs refreshing (within 5 minutes of expiry)
export const shouldRefreshToken = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.expires_at) return false;

    const expiresAt = new Date(session.expires_at).getTime();
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

    return (expiresAt - now) < fiveMinutes;
  } catch (err) {
    console.warn('Error checking token expiry:', err);
    return false;
  }
}

// Smart session getter that only refreshes when needed
export const getSessionSmart = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (session && await shouldRefreshToken()) {
      console.log('Token expiring soon, refreshing...');
      const { data } = await refreshSession();
      return data?.session || session;
    }

    return session;
  } catch (err) {
    console.error('Error in smart session getter:', err);
    return null;
  }
}
